import { create } from 'zustand';
import { combosService, type ComboData, type ComboListItem, type ProcessedComboByIdListItem, type ProcessedComboListItem, type BulkJobStatus } from '../services/combosService';
import { downloadFile } from '@/utils/file';

interface PaginationList<T> {
  list: T[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
  itemsPerPage: number;
}
interface CombosState {
  combos: ComboListItem[];
  loading: boolean;
  loadingDownload: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  searchQuery: string;

  saveCombo: (comboData: ComboData) => Promise<any>;
  getListCombos: (params?: { page?: number; page_size?: number; search?: string }) => Promise<any>;

  processedCombos: PaginationList<ProcessedComboListItem>;
  processedCombosById: PaginationList<ProcessedComboByIdListItem>;

  getProcessedCombos: (params?: { page?: number; page_size?: number; search?: string }) => Promise<any>;
  getProcessedCombosById: (id: number, type: 'manual' | 'bus', params?: { page?: number; page_size?: number; search?: string }) => Promise<any>;

  updateProcessedCombos: (updatedProcessedCombos: Partial<PaginationList<ProcessedComboListItem>>) => void;
  updateProcessedCombosById: (updatedProcessedCombosById: Partial<PaginationList<ProcessedComboByIdListItem>>) => void;

  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;

  downloadGroupedCombos: (comboIds?: string[], isFolder?: boolean, isManual?: boolean) => Promise<{ success: boolean; message: string }>;

  bulkJobStatus: BulkJobStatus | null;
  getBulkJobStatus: (processImageId: number) => Promise<void>;
}

export const useCombosStore = create<CombosState>((set, get) => ({
  combos: [],
  processedCombos: {
    list: [],
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 25,
  },
  processedCombosById: {
    list: [],
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 25,
  },
  loading: false,
  loadingDownload: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  itemsPerPage: 25,
  totalItems: 0,
  searchQuery: '',
  bulkJobStatus: null,

  saveCombo: async (comboData: ComboData) => {
    try {
      const result = await combosService.saveCombo(comboData);
      // Refrescar la lista después de guardar
      void get().getListCombos();
      return result;
    } catch (error) {
      return false;
    }
  },

  getListCombos: async (params?: { page?: number; page_size?: number; search?: string }) => {
    set({ loading: true, error: null });
    try {
      const { currentPage, itemsPerPage, searchQuery } = get();

      const finalParams = {
        page: params?.page || currentPage,
        page_size: params?.page_size || itemsPerPage,
        search: params?.search || searchQuery
      };

      const response = await combosService.getCombosList(finalParams);

      set({
        combos: response.results,
        totalItems: response.count,
        totalPages: Math.ceil(response.count / finalParams.page_size),
        loading: false
      });

      return response;
    } catch (error) {
      console.error('Error fetching Combos:', error);
      set({
        error: error instanceof Error ? error.message : 'Error desconocido al obtener combos',
        loading: false
      });
      throw error;
    }
  },

  getProcessedCombos: async (params?: { page?: number; page_size?: number; search?: string }) => {
    set({ loading: true, error: null });
    try {
      const { processedCombos } = get();
      const { currentPage, itemsPerPage } = processedCombos;

      const finalParams = {
        page: params?.page || currentPage,
        page_size: params?.page_size || itemsPerPage,
        currentPage: params?.page || currentPage,
      };

      const response = await combosService.getProcessedCombos(finalParams);
      set({
        processedCombos: {
          ...processedCombos,
          list: response.results,
          totalItems: response.count,
          totalPages: Math.ceil(response.count / finalParams.page_size),
          itemsPerPage: finalParams.page_size,
        },
        loading: false
      });
      return response;
    } catch (error) {
      console.error('Error fetching Processed Combos:', error);
      set({
        error: error instanceof Error ? error.message : 'Error desconocido al obtener combos procesados',
        loading: false
      });
      throw error;
    }
  },

  getProcessedCombosById: async (id: number, type: 'manual' | 'bus', params?: { page?: number; page_size?: number; search?: string }) => {
    set({ loading: true, error: null });
    try {
      const { processedCombosById } = get();
      const { currentPage, itemsPerPage } = processedCombosById;

      const finalParams = {
        page: params?.page || currentPage,
        page_size: params?.page_size || itemsPerPage,
      };

      const response = await combosService.getProcessedCombosById(id, type, finalParams);
      set({
        processedCombosById: {
          ...processedCombosById,
          list: response.results,
          totalItems: response.count,
          totalPages: Math.ceil(response.count / finalParams.page_size),
          itemsPerPage: finalParams.page_size,
        }, loading: false
      });
    } catch (error) {
      console.error('Error fetching Processed Combos by Id:', error);
      set({
        error: error instanceof Error ? error.message : 'Error desconocido al obtener combos procesados',
        loading: false
      });
      throw error;
    }
  },

  updateProcessedCombos: (updatedProcessedCombos: Partial<PaginationList<ProcessedComboListItem>>) => {
    const { processedCombos } = get();
    set({ processedCombos: { ...processedCombos, ...updatedProcessedCombos } });
  },
  updateProcessedCombosById: (updatedProcessedCombosById: Partial<PaginationList<ProcessedComboByIdListItem>>) => {
    const { processedCombosById } = get();
    set({ processedCombosById: { ...processedCombosById, ...updatedProcessedCombosById } });
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query, currentPage: 1 });
  },

  setCurrentPage: (page: number) => {
    set({ currentPage: page });
  },

  setItemsPerPage: (itemsPerPage: number) => {
    set({ itemsPerPage, currentPage: 1 });
  },

  downloadGroupedCombos: async (comboIds?: string[], isFolder?: boolean, isManual?: boolean) => {
    set({ loadingDownload: true });
    try {
      // get files queued
      const response = await combosService.downloadGroupedCombos(comboIds || [], isFolder, isManual);
      if (!response.status_url) {
        set({ loadingDownload: false });
        return {
          success: false,
          message: 'Error al iniciar el proceso de descarga',
        };
      }

      // Query queued file
      let statusResult = await combosService.getStatusCombos(response.status_url);
      while (statusResult.status === 'pending' || statusResult.status === 'started') {
        await new Promise(resolve => setTimeout(resolve, 5000));
        statusResult = await combosService.getStatusCombos(response.status_url);
      }

      // Catch error
      if (statusResult.status === 'error' || !statusResult.url) {
        set({ loadingDownload: false });
        return {
          success: false,
          message: 'Error al generar la URL de descarga',
        };
      }

      // Success download
      downloadFile(statusResult.url, 'combos.zip');
      set({ loadingDownload: false });
      return {
        success: true,
        message: 'Descarga exitosa',
      };

    } catch (error) {
      console.error('Error durante la descarga:', error);
      // Unexpected error
      set({ loadingDownload: false });
      return {
        success: false,
        message: 'Ocurrió un error al procesar la descarga',
      };
    }
  },

  getBulkJobStatus: async (processImageId: number) => {
    try {
      const status = await combosService.getBulkJobStatus(processImageId);
      set({ bulkJobStatus: status });
    } catch (error) {
      console.error('Error al obtener estado del proceso:', error);
      set({
        bulkJobStatus: null,
        error: error instanceof Error ? error.message : 'Error al obtener estado del proceso'
      });
    }
  }
}));
